import { LearnBlogPost, WordpressPost, WordpressPostSearchOptions } from '@arrived/models';

import { wordpressApi } from './api';

const unwrapWordpressListResponse = (response: WordpressPost[]): LearnBlogPost[] =>
  response?.map((item) => ({
    id: item.id,
    date: item.date,
    title: item.title.rendered,
    excerpt: item.excerpt.rendered,
    content: item.content.rendered,
    link: item.link,
    readingTime: item.yoast_head_json?.twitter_misc?.['Est. reading time'] ?? '',
    featuredImage:
      item.yoast_head_json?.og_image?.[0]?.url ?? (item._embedded ?? item._links)['wp:featuredmedia']?.[0]?.source_url,
  }));

export interface GetLeanBlogPostsParams extends WordpressPostSearchOptions {
  pageParam: number;
  perPage?: number;
  [key: string]: unknown;
}

export const getLearnBlogPosts = async ({ pageParam, perPage = 10, ...rest }: GetLeanBlogPostsParams) => {
  try {
    const response = await wordpressApi.get<WordpressPost[]>('wp-json/wp/v2/posts', {
      params: {
        _embed: true,
        page: pageParam,
        per_page: perPage,
        ...rest,
      },
    });

    const totalPages = parseInt(response.headers['x-wp-totalpages'], 10);

    return {
      data: unwrapWordpressListResponse(response.data),
      nextCursor: totalPages > pageParam ? pageParam + 1 : undefined,
      prevCursor: pageParam > 1 ? pageParam - 1 : undefined,
    };
  } catch (error) {
    console.error('Error fetching posts', error);

    return {
      data: [],
      nextCursor: undefined,
      prevCursor: undefined,
    };
  }
};

export interface GetLearnBlogArticleParams {
  date?: string;
  guid?: string;
  id?: number;
  link?: string;
  uri?: string;
  slug?: string;
  status?: 'publish' | 'future' | 'draft' | 'pending' | 'private';
  type?: string;
  permalink_template?: string;
  generated_slug?: string;
  title?: Record<string, unknown>;
  content?: Record<string, unknown>;
  author?: number;
  excerpt?: Record<string, unknown>;
  featured_media?: number;
  comment_status?: 'open' | 'closed';
  ping_status?: 'open' | 'closed';
  format?: string;
  meta?: Record<string, unknown>;
  sticky?: boolean;
  template?: string;
  categories?: number[];
  tags?: string[];
}

export const getLearnBlogArticle = async ({ id, ...params }: GetLearnBlogArticleParams) => {
  try {
    const response = await wordpressApi.get<WordpressPost[]>(`wp-json/wp/v2/posts`, {
      params: {
        include: id,
        ...params,
      },
    });

    return unwrapWordpressListResponse(response.data)[0];
  } catch (error) {
    console.error('Error fetching post', error);

    return null;
  }
};
